/**
 * L2 inputs — TextComposer · NumberField.
 *
 * The request's free-text half. Its peer — the chip row that contributes a
 * VALUE to the same request — is `OptionSelect` in `filters.jsx`: the "composes
 * a value" vs "filters a collection" distinction that used to keep them apart
 * turned out to be a fact about the blueprint's CONNECTION, not about the
 * component, and both defaults it rested on (`All` inclusion, "never land on
 * nothing selected") are now the props `allOption` and `clearable`.
 *
 * `NumberField` is the third reading of the same shape: compose ONE NUMBER and
 * submit it. It is not a variant of either neighbour — a chip ladder cannot
 * express a 521-value range, and free text cannot be validated against bounds —
 * so it is its own entry, with the same "disabled until there is something to
 * send" seal both of them already carry.
 */
import { useEffect, useRef, useState } from 'react';
import { fmt, resetButton, hitTarget } from './ui';

/**
 * TextComposer — multiline text + one submit.
 *
 * SEALED: submit is DISABLED while the text is empty. A composer whose button
 * is always live invites a request with nothing in it, and the host has no way
 * to tell that apart from a real one.
 *
 * `seed` is the other half of a chip picker: a chip fills the field, the user
 * edits it, and the same `submitted {text}` leaves — so a page never has to
 * decide whether the chip or the typing "won".
 */
export function TextComposer({ props = {}, data, emit, slots }) {
  const seed = props.seed !== undefined ? props.seed : data;
  const [text, setText] = useState(seed == null ? '' : String(seed));
  const [focused, setFocused] = useState(false);
  const pushed = slots?.setText;
  const seeded = useRef(seed);

  useEffect(() => {
    if (seed !== seeded.current) { seeded.current = seed; setText(seed == null ? '' : String(seed)); }
  }, [seed]);
  useEffect(() => {
    if (pushed?.payload?.text !== undefined) setText(String(pushed.payload.text ?? ''));
  }, [pushed]);

  const ready = text.trim().length > 0;
  const showSubmit = props.showSubmit !== false;
  const label = props.label == null ? '' : String(props.label);
  const description = props.description == null ? '' : String(props.description);
  const send = () => {
    if (!ready) return;
    emit?.('submitted', { text: text.trim() });
    if (props.clearOnSubmit !== false) setText('');
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
      background: 'var(--surface)', borderRadius: 'var(--r)', padding: 'var(--sp-3)',
      border: `1px solid ${focused ? 'var(--accent)' : 'var(--line)'}`,
      boxShadow: focused ? '0 0 0 3px var(--accent-soft)' : 'var(--shadow-1)', minWidth: 0,
    }}>
      {label ? <div style={{ fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--text)' }}>{label}</div> : null}
      {description ? <div style={{ fontSize: 'var(--t-xs)', lineHeight: 1.4, color: 'var(--text-3)' }}>{description}</div> : null}
      <textarea
        value={text}
        rows={Number(props.rows) || 3}
        placeholder={props.placeholder || 'Add a note'}
        aria-label={props.placeholder || label || 'Prompt'}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          // 只有外层 action bar 接管提交时才附带合法性，保持默认事件载荷不变。
          const payload = { text: next };
          if (!showSubmit) payload.valid = next.trim().length > 0;
          emit?.('textChanged', payload);
        }}
        style={{
          boxSizing: 'border-box', border: '1px solid var(--line)', borderRadius: 'var(--r)', outline: 'none',
          resize: 'none', background: 'var(--surface)', padding: '10px 12px', font: 'inherit',
          fontSize: 'var(--t-sm)', color: 'var(--text)', lineHeight: 1.5, minWidth: 0, width: '100%',
        }}
      />
      {showSubmit ? <button
        type="button"
        disabled={!ready}
        onClick={send}
        style={{
          ...resetButton, ...hitTarget,
          alignSelf: 'flex-end', padding: '9px 18px', borderRadius: 'var(--r-pill)',
          background: 'var(--accent)', color: 'var(--accent-ink)',
          fontWeight: 700, fontSize: 'var(--t-sm)',
          opacity: ready ? 1 : 0.4, cursor: ready ? 'pointer' : 'not-allowed',
        }}
      >{props.submitLabel || 'Send'}</button> : null}
    </div>
  );
}

/** The four readings the field can be in — the order is the check order. */
const HINT_LEAD = {
  empty: 'Enter a number between',
  range: 'Keep it between',
  unchanged: 'That’s already set',
  ok: 'New value',
};

/**
 * The hint SENTENCE — the blueprint supplies the lead phrase, this owns the
 * numbers.
 *
 * The same rule the overflow cell states in `collections.jsx`, for the same
 * reason: `{…}` in a string prop is the BLUEPRINT's data channel and
 * `resolveProps` interpolates it against the page context long before a
 * component runs, so a `"between {min} and {max}"` template would arrive here
 * as `"between  and "`. One brace grammar, one owner — and the composed
 * sentence is prod's copy verbatim once the blueprint passes its own leads.
 */
function hintText(kind, leads, { min, max, current, value, unit }) {
  const lead = String(leads?.[kind] ?? HINT_LEAD[kind]).trim();
  const tail = unit ? ` ${unit}` : '';
  if (kind === 'empty' || kind === 'range') return `${lead} ${fmt(min)} and ${fmt(max)}${tail}.`;
  if (kind === 'unchanged') return `${lead} (~${fmt(current)}${tail}).`;
  const delta = value - current;
  return `${lead}: ${fmt(value)}${tail} (${delta > 0 ? '+' : '−'}${fmt(Math.abs(delta))} from now).`;
}

/**
 * The submit's SUBMITTING ring — the persistent request state a save shows while
 * the write is in flight. The interpreter delivers `submitStatus {submitting}`
 * to the SOURCE control the moment its `committed` feeds a `host.logData`
 * `awaitConfirm` (KG-23), and clears it when the pushed feed confirms the write
 * or the confirmation times out. It never turns unless a blueprint declares that
 * confirmation, so a plain submit is unchanged.
 */
const SUBMIT_SPINNER = {
  width: 16, height: 16, borderRadius: '50%', flex: '0 0 auto',
  border: '2px solid color-mix(in srgb, currentColor 35%, transparent)',
  borderTopColor: 'currentColor',
  animation: 'viNativeSpin 0.8s linear infinite',
};

/** Snap to the `step` grid and clamp into `[min,max]` — the stepper's contract. */
function snap(value, { min, max, step }) {
  const grid = step > 0 ? Math.round(value / step) * step : value;
  return Math.max(min, Math.min(max, grid));
}

/**
 * NumberField — one bounded number, its `±` steppers, and a submit that is dead
 * until the number is both VALID and DIFFERENT (KG-27).
 *
 * SEALED, and it is the whole point of the component:
 *   · submit is disabled while the value is out of bounds, unparseable, or
 *     equal to the current one — a "save" that saves the same number is a
 *     write the user cannot tell from a no-op, and the host cannot either;
 *   · the steppers CLAMP and SNAP (`step` grid), so a held finger can never
 *     walk the value past the bound or off the grid;
 *   · TYPING is not snapped — prod accepts any in-range number typed by hand,
 *     and a field that rewrote your digits as you typed would be unusable.
 *
 * Props: `min` · `max` · `step` (the grid, default 1) · `stepBy` (what a `±`
 * press moves, default `step`) · `value` (a bind — the CURRENT committed
 * number) · `unit` · `hints` (the four lead phrases, see `hintText`) ·
 * `showHint`（默认显示派生提示）·
 * `submitLabel` · `label` (a11y).
 *
 * Signals: `committed {value,delta}` and `changed {value,valid}`.
 * Slot: `setValue {value}` — the same "push a value in" seam `TextComposer`
 * exposes as `setText`, and the one an honest write-confirmation lane needs to
 * re-seed the field after the feed comes back.
 */
export function NumberField({ props = {}, data, emit, slots }) {
  const current = Number(props.value !== undefined ? props.value : data);
  const min = Number.isFinite(Number(props.min)) ? Number(props.min) : 0;
  const max = Number.isFinite(Number(props.max)) ? Number(props.max) : 100;
  const step = Number(props.step) > 0 ? Number(props.step) : 1;
  const stepBy = Number(props.stepBy) > 0 ? Number(props.stepBy) : step;
  const unit = props.unit == null ? '' : String(props.unit);

  const seedOf = () => (Number.isFinite(current) ? String(current) : '');
  const [text, setText] = useState(seedOf);
  const seeded = useRef(current);
  const pushed = slots?.setValue;

  useEffect(() => {
    if (current !== seeded.current) { seeded.current = current; setText(Number.isFinite(current) ? String(current) : ''); }
  }, [current]);
  useEffect(() => {
    if (pushed?.payload?.value !== undefined) setText(String(pushed.payload.value ?? ''));
  }, [pushed]);

  const raw = text.trim() === '' ? NaN : Number(text);
  const kind = !Number.isFinite(raw) ? 'empty'
    : (raw < min || raw > max) ? 'range'
      : (raw === current ? 'unchanged' : 'ok');
  const ready = kind === 'ok';
  const showSubmit = props.showSubmit !== false;
  // 默认保留既有目标编辑提示；Birthday 这类收集参数的场景可单独关闭它。
  const showHint = props.showHint !== false;
  // The interpreter-delivered write state (KG-23): true from the frame this
  // field's `committed` armed a `host.logData` awaitConfirm until the write is
  // confirmed or times out. A submit whose write is in flight shows a spinner in
  // place of its label and is disabled.
  const submitting = Boolean(slots?.submitStatus?.payload?.submitting);

  const put = (next) => {
    setText(String(next));
    // 外层表单收集数值时，改回默认值也合法；独立目标编辑仍要求有变化。
    emit?.('changed', { value: next, valid: next >= min && next <= max && (!showSubmit || next !== current) });
  };
  const nudge = (delta) => {
    const base = Number.isFinite(raw) ? raw : current;
    put(snap((Number.isFinite(base) ? base : min) + delta, { min, max, step }));
  };
  const send = () => {
    if (!ready) return;
    emit?.('committed', { value: raw, delta: raw - current });
  };

  const stepper = {
    ...resetButton, ...hitTarget,
    width: 44, height: 44, borderRadius: 'var(--r-pill)',
    background: 'var(--surface-3)', color: 'var(--text)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 'var(--t-xl)', fontWeight: 700, lineHeight: 1,
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)',
      background: 'var(--surface)', borderRadius: 'var(--r)', padding: 'var(--sp-4)',
      boxShadow: 'var(--shadow-1)', minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', minWidth: 0 }}>
        <button type="button" style={stepper} aria-label={`Lower by ${stepBy}`} onClick={() => nudge(-stepBy)}>−</button>
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 2,
        }}>
          <input
            type="number"
            inputMode="numeric"
            value={text}
            min={min}
            max={max}
            step={step}
            aria-label={props.label || 'Value'}
            onChange={(e) => {
              const next = e.target.value;
              setText(next);
              const parsed = next.trim() === '' ? NaN : Number(next);
              emit?.('changed', {
                value: Number.isFinite(parsed) ? parsed : null,
                valid: Number.isFinite(parsed) && parsed >= min && parsed <= max && (!showSubmit || parsed !== current),
              });
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            style={{
              width: '100%', border: 'none', outline: 'none', background: 'transparent',
              font: 'inherit', fontSize: 'var(--t-3xl)', fontWeight: 800, lineHeight: 1,
              color: 'var(--text)', textAlign: 'center', fontVariantNumeric: 'tabular-nums',
              minWidth: 0, padding: 0,
            }}
          />
          {unit ? (
            <span style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--text-3)' }}>{unit}</span>
          ) : null}
        </div>
        <button type="button" style={stepper} aria-label={`Raise by ${stepBy}`} onClick={() => nudge(stepBy)}>+</button>
      </div>

      {showHint ? <div style={{
        fontSize: 'var(--t-sm)', lineHeight: 1.35, textAlign: 'center',
        color: kind === 'ok' ? 'var(--good)' : (kind === 'unchanged' ? 'var(--text-3)' : 'var(--warn)'),
      }}>
        {hintText(kind, props.hints, { min, max, current, value: raw, unit })}
      </div> : null}

      {showSubmit ? <button
        type="button"
        disabled={!ready || submitting}
        aria-busy={submitting || undefined}
        aria-label={props.submitLabel || 'Save'}
        onClick={send}
        style={{
          ...resetButton, ...hitTarget,
          padding: '11px 18px', borderRadius: 'var(--r-pill)',
          background: 'var(--accent)', color: 'var(--accent-ink)',
          fontWeight: 700, fontSize: 'var(--t-md)', textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: (ready || submitting) ? 1 : 0.4,
          cursor: (ready && !submitting) ? 'pointer' : 'not-allowed',
        }}
      >{submitting
        ? <span aria-hidden style={SUBMIT_SPINNER} />
        : (props.submitLabel || 'Save')}</button> : null}
    </div>
  );
}
